import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { SEMANTIC_BUCKETS } from "@/lib/statusSemantics";

// v1.2 Part 2: "By the Way..." — a single, friendly, non-blocking inline
// nudge (never a toast, never more than one visible per screen). Dismissal
// persists only for the current browser session (sessionStorage) so the
// same nudge won't reappear if closed, but returns on the next visit.
//
// Security note: nudge ids are content-derived (a slug of the nudge
// message, which can include names/amounts), so we never persist the raw
// id — only a one-way hash of it — to avoid keeping readable sensitive
// text sitting in browser storage.
const DISMISS_KEY = "ddh_bytheway_dismissed";

// Module-level constants (not recreated every render) — passed as-is into
// framer-motion's animation props below.
const NUDGE_MOTION_INITIAL = { opacity: 0, y: 8 };
const NUDGE_MOTION_ANIMATE = { opacity: 1, y: 0 };
const NUDGE_MOTION_TRANSITION = { duration: 0.2, ease: "easeOut" };

// Simple, fast, deterministic one-way string hash (not cryptographic —
// just enough to avoid storing readable nudge content in sessionStorage).
const hashNudgeId = (value) => {
  let hash = 0;
  const str = String(value || "");
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};

const getDismissed = () => {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISS_KEY) || "[]");
  } catch (err) {
    console.error("ByTheWayNudge: failed to read dismissed nudges from sessionStorage", err);
    return [];
  }
};

export const ByTheWayNudge = ({ nudge }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(getDismissed);

  if (!nudge || dismissed.includes(hashNudgeId(nudge.id))) return null;

  const sem = SEMANTIC_BUCKETS[nudge.bucketKey] || SEMANTIC_BUCKETS.scheduled_upcoming;
  const Icon = sem.icon;

  const handleDismiss = (e) => {
    e.stopPropagation();
    const next = [...dismissed, hashNudgeId(nudge.id)];
    setDismissed(next);
    try {
      sessionStorage.setItem(DISMISS_KEY, JSON.stringify(next));
    } catch (err) {
      // sessionStorage unavailable — dismissal just won't persist, non-critical.
      console.error("ByTheWayNudge: failed to persist dismissed nudges to sessionStorage", err);
    }
  };

  const handleClick = () => {
    if (nudge.action?.route) navigate(nudge.action.route);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={NUDGE_MOTION_INITIAL}
        animate={NUDGE_MOTION_ANIMATE}
        transition={NUDGE_MOTION_TRANSITION}
      >
        <Alert
          onClick={nudge.action ? handleClick : undefined}
          className={`flex items-start gap-3 border border-l-4 ${sem.leftBorder} ${sem.rowTint} py-2.5 px-3 ${nudge.action ? "cursor-pointer hover:brightness-[0.98]" : ""}`}
          data-testid="by-the-way-nudge"
        >
          <Icon className="mt-0.5 h-4 w-4 text-muted-foreground shrink-0" />
          <p className="text-sm pr-6 leading-relaxed">{nudge.message}</p>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1.5 top-1.5 h-6 w-6"
            onClick={handleDismiss}
            aria-label="Dismiss"
            data-testid="by-the-way-nudge-dismiss-button"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </Alert>
      </motion.div>
    </AnimatePresence>
  );
};
